import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { 
  BuildingOfficeIcon,
  HomeIcon,
  UserGroupIcon,
  HeartIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { sectorService, Sector } from '../services/sectorService';
import { Sector as SectorEnum, SECTOR_LABELS } from '../types';

// Icon mapping based on sector type
const getSectorIcon = (sectorName: string) => {
  const name = sectorName.toLowerCase();
  if (name.includes('children') || name.includes('child')) {
    return HomeIcon;
  }
  if (name.includes('supported') || name.includes('accommodation')) {
    return BuildingOfficeIcon;
  }
  if (name.includes('domiciliary') || name.includes('home care')) {
    return HeartIcon;
  }
  if (name.includes('adult residential') || name.includes('nursing')) {
    return BuildingOfficeIcon;
  }
  if (name.includes('fostering')) {
    return UserGroupIcon;
  }
  if (name.includes('day centre') || name.includes('community')) {
    return BuildingOfficeIcon;
  }
  if (name.includes('sen') || name.includes('specialist') || name.includes('education')) {
    return AcademicCapIcon;
  }
  if (name.includes('mental health')) {
    return SparklesIcon;
  }
  return BuildingOfficeIcon; // Default icon
};

// Reverse mapping from sector label to enum value
const getSectorEnum = (sectorName: string): SectorEnum | null => {
  const entries = Object.entries(SECTOR_LABELS) as [SectorEnum, string][];
  const found = entries.find(([_, label]) => label === sectorName);
  return found ? found[0] : null;
};

export const SectorsPage: React.FC = () => {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSectors = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await sectorService.getAllSectors();
        setSectors(response.sectors || []);
      } catch (err: any) {
        console.error('Error loading sectors:', err);
        setError(err.message || 'Failed to load sectors. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSectors();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-secondary-900 mb-4">
          Browse Sectors
        </h1>
        <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
          Explore care sectors across the UK. Find resources tailored to your specific 
          regulatory requirements and sector needs.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600 text-lg">Loading sectors...</p>
          </div>
        </div>
      ) : sectors.length > 0 ? (
        <>
          {/* Sectors Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {sectors.map((sector) => {
              const IconComponent = getSectorIcon(sector.name);
              const sectorEnum = getSectorEnum(sector.name);
              const sectorParam = sectorEnum || sector.name;
              
              return (
                <Link
                  key={sector.id}
                  to={`/marketplace?sector=${encodeURIComponent(sectorParam)}`}
                  className="block"
                >
                  <Card hover className="h-full flex flex-col transition-all duration-200 hover:scale-105">
                    <div className="p-6 h-full flex flex-col">
                      {/* Icon and Regulator Badge */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 bg-primary-50 rounded-lg">
                          <IconComponent className="h-6 w-6 text-primary-600" />
                        </div>
                        {sector.regulator && (
                          <Badge variant="primary" size="sm">
                            <ShieldCheckIcon className="h-3 w-3 mr-1" />
                            {sector.regulator}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Sector Name */}
                      <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                        {sector.name}
                      </h3>
                      
                      {/* Description */}
                      {sector.description && (
                        <p className="text-sm text-secondary-600 mb-4 flex-grow">
                          {sector.description}
                        </p>
                      )}
                      
                      {/* View Sector Link */}
                      <div className="flex items-center text-primary-600 font-medium text-sm mt-auto">
                        <span>Explore Products</span>
                        <ArrowRightIcon className="h-4 w-4 ml-2" />
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Stats Section */}
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                {sectors.length} Sectors Available
              </h2>
              <p className="text-secondary-600">
                Browse through our comprehensive collection of UK care sectors, 
                each with resources tailored to specific regulatory requirements.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-20">
          <div className="text-secondary-400 mb-4">
            <BuildingOfficeIcon className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-secondary-900 mb-2">
            No sectors found
          </h3>
          <p className="text-secondary-600">
            Sectors will appear here once they are added to the system.
          </p>
        </div>
      )}
    </div>
  );
};

